import React, { useEffect, useState } from "react";

import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";

import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import api from "@/lib/api";

import { asArray, formatCount } from "@/lib/safeData";

import { Bookmark, MessageSquare, FileText, Star, ChevronRight, ShieldAlert, User as UserIcon, ShieldCheck, Scale } from "lucide-react";

import { Button } from "@/components/ui/button";

import TrustScore from "@/components/TrustScore";

import CaseWorkspace from "@/components/CaseWorkspace";
import { formatNotification } from "@/i18n/formatNotification";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";



const Dashboard = () => {

  const { user } = useAuth();

  const { t, statusLabel, lang } = useT();
  const [searchParams] = useSearchParams();

  const d = t.dashboard || {};
  const ntx = t.notifications || {};

  const [reviews, setReviews] = useState([]);

  const [cases, setCases] = useState([]);

  const [saved, setSaved] = useState([]);

  const [legalRequests, setLegalRequests] = useState([]);

  const [openCase, setOpenCase] = useState(null);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const [prefs, setPrefs] = useState({ blog: true, case_updates: true });

  const loadDashboard = () => {
    api.get("/me/reviews").then((r) => setReviews(asArray(r.data))).catch(() => setReviews([]));
    api.get("/me/cases").then((r) => setCases(asArray(r.data))).catch(() => setCases([]));
    api.get("/me/saved-mentors").then((r) => setSaved(asArray(r.data))).catch(() => setSaved([]));
    api.get("/me/legal-requests").then((r) => setLegalRequests(asArray(r.data))).catch(() => setLegalRequests([]));
    api.get("/notifications", { params: { limit: 6 } }).then((r) => setRecentNotifs(r.data || [])).catch(() => setRecentNotifs([]));
    api.get("/me/notification-prefs").then((r) => setPrefs(r.data || { blog: true, case_updates: true })).catch(() => {});
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    const raw = searchParams.get("open");
    if (!raw || !raw.includes(":")) return;
    const [type, id] = raw.split(":");
    if (!id || (type !== "scam_report" && type !== "legal_request")) return;
    const scam = cases.find((c) => c.id === id);
    const legal = legalRequests.find((c) => c.id === id);
    const title =
      type === "scam_report"
        ? scam?.scam_type || "Scam report"
        : legal?.loss_type || legal?.name || "Legal help";
    setOpenCase({ type, id, title });
  }, [searchParams, cases, legalRequests]);

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await api.patch("/me/notification-prefs", patch);
      toast.success(d.prefsSaved || "Preferences saved");
    } catch {
      toast.error(t.common?.error || "Something went wrong");
    }
  };



  if (!user) return null;



  const langCls = langClass(lang);



  const stats = [

    { label: d.statReviews, value: reviews.length, icon: MessageSquare },

    { label: d.statCases, value: cases.length, icon: FileText },

    { label: d.statLegal, value: legalRequests.length, icon: Scale },

    { label: d.statSaved, value: saved.length, icon: Bookmark },

  ];



  return (

    <div className="mx-auto max-w-6xl px-4 md:px-8 py-12 md:py-16" data-testid="dashboard-page">

      <div className="flex flex-wrap items-center justify-between gap-4">

        <div className="flex items-center gap-4">

          <div className="h-14 w-14 rounded-full ring-1 ring-white/15 bg-white/5 grid place-items-center overflow-hidden">

            {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <UserIcon className="h-6 w-6 text-white/60" />}

          </div>

          <div>

            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt">{d.eyebrow}</div>

            <h1 className={`font-display text-2xl md:text-3xl font-black ${langCls}`}>

              {d.hello}, {user.name}

            </h1>

            <div className="text-xs text-white/50 mt-1 font-mono-alt">{user.email} · {user.provider}</div>

          </div>

        </div>

      </div>



      {user.role === "admin" && (

        <Link to="/admin" className="mt-6 block surface-card p-5 border border-[#FF6A60]/25 hover:border-[#FF6A60]/50 transition-colors" data-testid="dashboard-admin-link">

          <div className="flex flex-wrap items-center justify-between gap-4">

            <div className="flex items-center gap-3">

              <ShieldCheck className="h-6 w-6 text-[#FF6A60]" />

              <div>

                <div className={`font-display text-lg font-bold text-white ${langCls}`}>{d.adminPanel}</div>

                <div className={`text-sm text-white/60 ${langCls}`}>{d.adminDesc}</div>

              </div>

            </div>

            <Button className="bg-[#FF6A60] hover:bg-[#cc2e25] text-white font-bold gap-2">

              {d.openAdmin} <ChevronRight className="h-4 w-4" />

            </Button>

          </div>

        </Link>

      )}



      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">

        {stats.map(({ label, value, icon: Icon }) => (

          <div key={label} className="surface-card p-5">

            <Icon className="h-5 w-5 text-[#D4AF37]" />

            <div className="mt-3 font-display text-3xl font-black">{value}</div>

            <div className={`text-[11px] uppercase tracking-widest text-white/50 font-mono-alt mt-1 ${langCls}`}>{label}</div>

          </div>

        ))}

      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card p-5">
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt mb-3">{d.recentActivity || "Recent updates"}</div>
          {recentNotifs.length === 0 ? (
            <p className="text-sm text-white/50">{ntx.empty || "No notifications yet"}</p>
          ) : (
            <ul className="space-y-2">
              {recentNotifs.map((n) => {
                const { title, body } = formatNotification(n, t, statusLabel);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      className="w-full text-left rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 hover:border-[#D4AF37]/40"
                      onClick={() => {
                        if (n.case_type && n.case_id) {
                          setOpenCase({
                            type: n.case_type,
                            id: n.case_id,
                            title: title.slice(0, 60),
                          });
                        }
                      }}
                    >
                      <div className="text-sm font-medium text-white/90">{title}</div>
                      <div className="text-xs text-white/55 line-clamp-1 mt-0.5">{body}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="surface-card p-5 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt">{d.prefsTitle || "Notification preferences"}</div>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm text-white/75">{d.prefsCase || "Case updates"}</Label>
            <Switch checked={!!prefs.case_updates} onCheckedChange={(v) => savePrefs({ case_updates: v })} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm text-white/75">{d.prefsBlog || "New articles"}</Label>
            <Switch checked={!!prefs.blog} onCheckedChange={(v) => savePrefs({ blog: v })} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="reviews" className="mt-10">

        <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-max min-w-full flex-nowrap h-auto bg-white/[0.04] border border-white/10">

          <TabsTrigger value="reviews" data-testid="dash-tab-reviews" className="shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">{d.tabReviews}</TabsTrigger>

          <TabsTrigger value="cases" data-testid="dash-tab-cases" className="shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">{d.tabCases}</TabsTrigger>

          <TabsTrigger value="legal" data-testid="dash-tab-legal" className="shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">{d.tabLegal}</TabsTrigger>

          <TabsTrigger value="saved" data-testid="dash-tab-saved" className="shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">{d.tabSaved}</TabsTrigger>

          </TabsList>
        </div>



        <TabsContent value="reviews" className="mt-6">

          {reviews.length === 0 ? (

            <div className={`surface-card p-10 text-center text-white/55 ${langCls}`}>

              {d.emptyReviews}{" "}

              <Link to="/mentors" className="text-[#D4AF37] hover:underline">{d.browseMentors} →</Link>

            </div>

          ) : (

            <div className="space-y-3">

              {reviews.map((r) => (

                <div key={r.id} className="surface-card p-5" data-testid={`dash-review-${r.id}`}>

                  <div className="flex items-center justify-between gap-3 flex-wrap">

                    <div>

                      <Link to={`/mentors/${r.mentor?.slug || r.mentor_id}`} className="text-sm text-[#D4AF37] hover:underline">

                        {r.mentor?.name || r.mentor_id}

                      </Link>

                      <div className="font-display text-base font-bold">{r.title}</div>

                    </div>

                    <div className="inline-flex items-center gap-1 text-[#D4AF37] font-mono-alt"><Star className="h-4 w-4 fill-current" /> {r.avg_rating}/5</div>

                  </div>

                  <p className="mt-2 text-sm text-white/70 line-clamp-2">{r.body}</p>

                </div>

              ))}

            </div>

          )}

        </TabsContent>



        <TabsContent value="cases" className="mt-6">

          {cases.length === 0 ? (

            <div className={`surface-card p-10 text-center text-white/55 ${langCls}`}>

              {d.emptyCases}{" "}

              <Link to="/#loss-categories" className="text-[#D4AF37] hover:underline">{d.reportScam} →</Link>

            </div>

          ) : (

            <div className="space-y-3">

              {cases.map((c) => (

                <button
                  key={c.id}
                  type="button"
                  onClick={() => setOpenCase({ type: "scam_report", id: c.id, title: c.scam_type })}
                  className="w-full text-left surface-card p-5 flex items-center justify-between gap-4 hover:border-[#D4AF37]/30 transition-colors"
                  data-testid={`dash-case-${c.id}`}
                >

                  <div className="min-w-0">

                    <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] font-mono-alt uppercase tracking-widest"><ShieldAlert className="h-3.5 w-3.5" /> {c.scam_type}</div>

                    <div className="mt-1 text-sm text-white/80 truncate">{c.description?.slice(0, 100)}…</div>

                    <div className="mt-1 text-[10px] text-white/40 font-mono-alt">{statusLabel(c.status) || c.status} · tap for timeline & messages</div>

                  </div>

                  <div className="text-[#FF6A60] font-mono-alt font-bold shrink-0">₹{formatCount(c.amount_lost)}</div>

                </button>

              ))}

            </div>

          )}

        </TabsContent>



        <TabsContent value="legal" className="mt-6">

          {legalRequests.length === 0 ? (

            <div className={`surface-card p-10 text-center text-white/55 ${langCls}`}>

              {d.emptyLegal}{" "}

              <Link to="/loss/other" className="text-[#D4AF37] hover:underline">{d.submitLegal} →</Link>

            </div>

          ) : (

            <div className="space-y-3">

              {legalRequests.map((r, i) => (

                <button
                  key={r.id ?? `legal-${i}`}
                  type="button"
                  onClick={() => setOpenCase({ type: "legal_request", id: r.id, title: r.loss_type })}
                  className="w-full text-left surface-card p-5 hover:border-[#D4AF37]/30 transition-colors"
                  data-testid={`dash-legal-${r.id ?? i}`}
                >

                  <div className="flex flex-wrap items-start justify-between gap-3">

                    <div className="min-w-0">

                      <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] font-mono-alt uppercase tracking-widest">

                        <Scale className="h-3.5 w-3.5" /> {r.loss_type}
                        {r.loss_category && (
                          <span className="text-white/40 normal-case">· {r.loss_category}</span>
                        )}

                      </div>

                      <div className="mt-1 text-sm text-white/80 line-clamp-2">{r.description || "—"}</div>

                      <div className="mt-1 text-[10px] text-white/40 font-mono-alt">

                        {statusLabel(r.status) || r.status} · tap for timeline & messages

                      </div>

                    </div>

                    <div className="text-right">

                      <div className="text-[#FF6A60] font-mono-alt font-bold">₹{formatCount(r.amount_lost)}</div>

                      {r.call_from_agent && (

                        <div className="mt-1 text-[10px] text-[#FF6A60]">{d.agentContact}</div>

                      )}

                    </div>

                  </div>

                </button>

              ))}

            </div>

          )}

        </TabsContent>



        <TabsContent value="saved" className="mt-6">

          {saved.length === 0 ? (

            <div className={`surface-card p-10 text-center text-white/55 ${langCls}`}>

              {d.emptySaved}{" "}

              <Link to="/mentors" className="text-[#D4AF37] hover:underline">{d.discoverMentors} →</Link>

            </div>

          ) : (

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {saved.map((m) => (

                <Link to={`/mentors/${m.slug}`} key={m.id} className="surface-card p-5 group block" data-testid={`dash-saved-${m.id}`}>

                  <div className="flex items-center justify-between">

                    <div>

                      <div className="font-display font-bold">{m.name}</div>

                      <div className="text-xs text-white/50">{m.company}</div>

                    </div>

                    <TrustScore trust={m.trust} size="sm" showLabel={false} />

                  </div>

                  <div className="mt-3 text-xs text-white/50 inline-flex items-center gap-1 group-hover:text-[#D4AF37] transition-colors">

                    {d.viewProfile} <ChevronRight className="h-3 w-3" />

                  </div>

                </Link>

              ))}

            </div>

          )}

        </TabsContent>

      </Tabs>

      <Dialog open={!!openCase} onOpenChange={(v) => !v && setOpenCase(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{openCase?.title || "Your case"}</DialogTitle>
            <DialogDescription className="text-white/55">
              Status timeline and messages with CHINTAMAT staff
            </DialogDescription>
          </DialogHeader>
          {openCase && (
            <CaseWorkspace
              caseType={openCase.type}
              caseId={openCase.id}
              onUpdated={() => {
                api.get("/me/cases").then((r) => setCases(asArray(r.data))).catch(() => {});
                api.get("/me/legal-requests").then((r) => setLegalRequests(asArray(r.data))).catch(() => {});
              }}
            />
          )}
        </DialogContent>
      </Dialog>

    </div>

  );

};



export default Dashboard;

